/**
 * Normalizes image identifiers to stable string keys.
 * @param {string|number} imageId
 * @returns {string}
 */
const toImageKey = imageId => String(imageId);

/**
 * Deduplicates image ids while preserving original order.
 * @param {Array<string|number>} imageIds
 * @returns {Array<string|number>}
 */
const uniqueImageIds = imageIds => {
  const seen = new Set();
  const result = [];
  (imageIds || []).forEach(imageId => {
    const key = toImageKey(imageId);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(imageId);
    }
  });
  return result;
};

/**
 * Clamps a slice index in the [0, maxInclusive] range.
 * @param {number} index
 * @param {number} maxInclusive
 * @returns {number}
 */
const clampSliceIndex = (index, maxInclusive) => {
  if (!Number.isFinite(maxInclusive) || maxInclusive < 0) {
    return 0;
  }
  const parsed = Number.isFinite(index) ? Math.floor(index) : 0;
  return Math.min(maxInclusive, Math.max(0, parsed));
};

/**
 * Converts a raw frames-per-slice value into a valid integer >= 1.
 * @param {number|string} rawValue
 * @returns {number}
 */
const normalizeFramesPerSlice = rawValue => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    return 1;
  }
  const normalized = Math.floor(parsed);
  return normalized > 1 ? normalized : 1;
};

/**
 * Resolves frames-per-slice from explicit options or inferred metadata.
 * @param {Object} params
 * @returns {number}
 */
const resolveFramesPerSlice = ({ options = {}, declaredCount = 0 } = {}) => {
  const explicit = normalizeFramesPerSlice(
    options.framesPerSlice ??
      options.numberOfTemporalPositions ??
      options.temporalPositions
  );
  if (explicit > 1) {
    return explicit;
  }

  const numberOfSlices = Number(options.numberOfSlices);
  if (!Number.isFinite(numberOfSlices) || numberOfSlices <= 0) {
    return 1;
  }
  const normalizedSlices = Math.floor(numberOfSlices);
  if (normalizedSlices <= 0 || declaredCount <= 0) {
    return 1;
  }
  if (declaredCount % normalizedSlices !== 0) {
    return 1;
  }
  const inferred = Math.floor(declaredCount / normalizedSlices);
  return inferred > 1 ? inferred : 1;
};

/**
 * Builds center-out traversal indexes from an optional anchor index.
 * @param {number} totalItems
 * @param {number|null} [anchorIndex]
 * @returns {number[]}
 */
const buildCenterOutIndexes = (totalItems, anchorIndex = null) => {
  if (!Number.isFinite(totalItems) || totalItems <= 0) {
    return [];
  }
  const maxIndex = Math.floor(totalItems) - 1;
  const anchor =
    anchorIndex === null || anchorIndex === undefined
      ? Math.floor(maxIndex / 2)
      : clampSliceIndex(anchorIndex, maxIndex);
  const order = [anchor];
  for (let distance = 1; order.length < totalItems; distance += 1) {
    const nextIndex = anchor + distance;
    if (nextIndex <= maxIndex) {
      order.push(nextIndex);
    }
    const previousIndex = anchor - distance;
    if (previousIndex >= 0) {
      order.push(previousIndex);
    }
  }
  return order;
};

/**
 * Builds priority download order around center slices.
 * @param {Object} params
 * @returns {Array<string|number>}
 */
const buildPriorityPendingOrder = ({
  declaredImageOrder,
  pendingImageIds,
  anchorSliceIndex = null,
  framesPerSlice = 1
}) => {
  const pending = uniqueImageIds(pendingImageIds || []);
  if (!pending.length) {
    return [];
  }

  const declared = uniqueImageIds(
    Array.isArray(declaredImageOrder) && declaredImageOrder.length
      ? declaredImageOrder
      : pending
  );
  if (!declared.length) {
    return pending;
  }

  const pendingKeys = new Set(pending.map(toImageKey));
  const prioritized = [];
  const frameGroupSize = normalizeFramesPerSlice(framesPerSlice);
  if (frameGroupSize > 1) {
    const totalSlices = Math.ceil(declared.length / frameGroupSize);
    const anchorSliceNumber = Number.isFinite(anchorSliceIndex)
      ? Math.floor(anchorSliceIndex / frameGroupSize)
      : null;
    buildCenterOutIndexes(totalSlices, anchorSliceNumber).forEach(sliceIndex => {
      const sliceStart = sliceIndex * frameGroupSize;
      const sliceEnd = Math.min(sliceStart + frameGroupSize, declared.length);
      for (
        let declaredIndex = sliceStart;
        declaredIndex < sliceEnd;
        declaredIndex += 1
      ) {
        const imageId = declared[declaredIndex];
        const key = toImageKey(imageId);
        if (pendingKeys.has(key)) {
          prioritized.push(imageId);
          pendingKeys.delete(key);
        }
      }
    });
  } else {
    buildCenterOutIndexes(declared.length, anchorSliceIndex).forEach(index => {
      const imageId = declared[index];
      const key = toImageKey(imageId);
      if (pendingKeys.has(key)) {
        prioritized.push(imageId);
        pendingKeys.delete(key);
      }
    });
  }
  pending.forEach(imageId => {
    const key = toImageKey(imageId);
    if (pendingKeys.has(key)) {
      prioritized.push(imageId);
      pendingKeys.delete(key);
    }
  });
  return prioritized;
};

/**
 * Builds priority order focused on a specific timeframe in multi-frame series.
 * @param {Object} params
 * @returns {Array<string|number>}
 */
const buildPriorityPendingOrderForTimeFrame = ({
  declaredImageOrder,
  pendingImageIds,
  timeFrameIndex = 0,
  anchorSliceIndex = null,
  framesPerSlice = 1
}) => {
  const pending = uniqueImageIds(pendingImageIds || []);
  if (!pending.length) {
    return [];
  }
  const declared = uniqueImageIds(
    Array.isArray(declaredImageOrder) && declaredImageOrder.length
      ? declaredImageOrder
      : pending
  );
  if (!declared.length) {
    return pending;
  }

  const frameGroupSize = normalizeFramesPerSlice(framesPerSlice);
  if (frameGroupSize <= 1) {
    return buildPriorityPendingOrder({
      declaredImageOrder: declared,
      pendingImageIds: pending,
      anchorSliceIndex,
      framesPerSlice: frameGroupSize
    });
  }

  const pendingKeys = new Set(pending.map(toImageKey));
  const prioritized = [];
  const totalSlices = Math.ceil(declared.length / frameGroupSize);
  const normalizedTimeFrameIndex = clampSliceIndex(
    timeFrameIndex,
    frameGroupSize - 1
  );
  const anchorSliceNumber = Number.isFinite(anchorSliceIndex)
    ? Math.floor(anchorSliceIndex / frameGroupSize)
    : null;

  buildCenterOutIndexes(totalSlices, anchorSliceNumber).forEach(sliceIndex => {
    const declaredIndex = sliceIndex * frameGroupSize + normalizedTimeFrameIndex;
    if (declaredIndex < 0 || declaredIndex >= declared.length) {
      return;
    }
    const imageId = declared[declaredIndex];
    const key = toImageKey(imageId);
    if (pendingKeys.has(key)) {
      prioritized.push(imageId);
      pendingKeys.delete(key);
    }
  });

  const remainingPending = pending.filter(imageId =>
    pendingKeys.has(toImageKey(imageId))
  );
  const rest = buildPriorityPendingOrder({
    declaredImageOrder: declared,
    pendingImageIds: remainingPending,
    anchorSliceIndex,
    framesPerSlice: frameGroupSize
  });
  return prioritized.concat(rest);
};

/**
 * Builds 4D priority order using double center-out around active scroll axis.
 * Active axis is traversed first for each fixed position on the other axis.
 * @param {Object} params
 * @returns {Array<string|number>}
 */
const buildPriorityPendingOrderForActiveAxis = ({
  declaredImageOrder,
  pendingImageIds,
  activeAxis = "frame",
  anchorSliceIndex = null,
  anchorFrameIndex = null,
  framesPerSlice = 1
}) => {
  const pending = uniqueImageIds(pendingImageIds || []);
  if (!pending.length) {
    return [];
  }
  const declared = uniqueImageIds(
    Array.isArray(declaredImageOrder) && declaredImageOrder.length
      ? declaredImageOrder
      : pending
  );
  if (!declared.length) {
    return pending;
  }

  const frameGroupSize = normalizeFramesPerSlice(framesPerSlice);
  if (frameGroupSize <= 1) {
    return buildPriorityPendingOrder({
      declaredImageOrder: declared,
      pendingImageIds: pending,
      anchorSliceIndex,
      framesPerSlice: frameGroupSize
    });
  }

  const normalizedActiveAxis = activeAxis === "slice" ? "slice" : "frame";
  const pendingKeys = new Set(pending.map(toImageKey));
  const prioritized = [];
  const totalSlices = Math.ceil(declared.length / frameGroupSize);
  const normalizedAnchorSliceIndex = Number.isFinite(anchorSliceIndex)
    ? clampSliceIndex(anchorSliceIndex, totalSlices - 1)
    : null;
  const normalizedAnchorFrameIndex = Number.isFinite(anchorFrameIndex)
    ? clampSliceIndex(anchorFrameIndex, frameGroupSize - 1)
    : null;
  const sliceOrder = buildCenterOutIndexes(
    totalSlices,
    normalizedAnchorSliceIndex
  );
  const frameOrder = buildCenterOutIndexes(
    frameGroupSize,
    normalizedAnchorFrameIndex
  );

  if (normalizedActiveAxis === "frame") {
    sliceOrder.forEach(sliceIndex => {
      frameOrder.forEach(frameIndex => {
        const declaredIndex = sliceIndex * frameGroupSize + frameIndex;
        if (declaredIndex < 0 || declaredIndex >= declared.length) {
          return;
        }
        const imageId = declared[declaredIndex];
        const key = toImageKey(imageId);
        if (pendingKeys.has(key)) {
          prioritized.push(imageId);
          pendingKeys.delete(key);
        }
      });
    });
  } else {
    frameOrder.forEach(frameIndex => {
      sliceOrder.forEach(sliceIndex => {
        const declaredIndex = sliceIndex * frameGroupSize + frameIndex;
        if (declaredIndex < 0 || declaredIndex >= declared.length) {
          return;
        }
        const imageId = declared[declaredIndex];
        const key = toImageKey(imageId);
        if (pendingKeys.has(key)) {
          prioritized.push(imageId);
          pendingKeys.delete(key);
        }
      });
    });
  }

  pending.forEach(imageId => {
    const key = toImageKey(imageId);
    if (pendingKeys.has(key)) {
      prioritized.push(imageId);
      pendingKeys.delete(key);
    }
  });
  return prioritized;
};

/**
 * Builds declared image index map for O(1) declared-slice lookup.
 * @param {Array<string|number>} declaredImageOrder
 * @returns {Map<string, number>}
 */
const buildDeclaredImageIndexMap = declaredImageOrder => {
  const map = new Map();
  (declaredImageOrder || []).forEach((imageId, index) => {
    const key = toImageKey(imageId);
    if (!map.has(key)) {
      map.set(key, index);
    }
  });
  return map;
};

/**
 * Returns declared slice index for a series/image pair.
 * @param {Object} seriesDeclaredImageIndexMaps
 * @param {string|number} seriesId
 * @param {string|number} dbImageId
 * @returns {number|null}
 */
const getDeclaredSliceIndex = (
  seriesDeclaredImageIndexMaps,
  seriesId,
  dbImageId
) => {
  const map = seriesDeclaredImageIndexMaps[seriesId];
  if (!map) {
    return null;
  }
  const index = map.get(toImageKey(dbImageId));
  return Number.isInteger(index) ? index : null;
};

/**
 * Returns sorted declared indexes currently renderable for a series.
 * @param {Object} seriesDeclaredImageIndexMaps
 * @param {Object} seriesRenderableImageIds
 * @param {string|number} seriesId
 * @returns {number[]}
 */
const getSeriesRenderableSliceIndexes = (
  seriesDeclaredImageIndexMaps,
  seriesRenderableImageIds,
  seriesId
) => {
  const declaredIndexMap = seriesDeclaredImageIndexMaps[seriesId];
  const renderableSet = seriesRenderableImageIds[seriesId];
  if (!declaredIndexMap || !renderableSet || !renderableSet.size) {
    return [];
  }
  const indexes = [];
  renderableSet.forEach(key => {
    const index = declaredIndexMap.get(key);
    if (Number.isInteger(index) && index >= 0) {
      indexes.push(index);
    }
  });
  indexes.sort((left, right) => left - right);
  return indexes;
};

/**
 * Compacts sorted indexes into contiguous runs.
 * @param {number[]} indexes
 * @returns {Array<{start:number,end:number}>}
 */
const buildIndexRuns = indexes => {
  if (!Array.isArray(indexes) || !indexes.length) {
    return [];
  }
  const sorted = Array.from(
    new Set(
      indexes.filter(index => Number.isInteger(index) && index >= 0)
    )
  ).sort((left, right) => left - right);
  if (!sorted.length) {
    return [];
  }
  const runs = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    if (current === end + 1) {
      end = current;
      continue;
    }
    runs.push({ start, end });
    start = current;
    end = current;
  }
  runs.push({ start, end });
  return runs;
};

/**
 * Marks an image as renderable for a series.
 * @param {Object} seriesRenderableImageIds
 * @param {string|number} seriesId
 * @param {string|number} dbImageId
 * @returns {void}
 */
const addSeriesRenderableImage = (seriesRenderableImageIds, seriesId, dbImageId) => {
  if (
    seriesId === null ||
    seriesId === undefined ||
    dbImageId === null ||
    dbImageId === undefined
  ) {
    return;
  }
  if (!seriesRenderableImageIds[seriesId]) {
    seriesRenderableImageIds[seriesId] = new Set();
  }
  seriesRenderableImageIds[seriesId].add(toImageKey(dbImageId));
};

/**
 * Rebuilds compact declared-order projection aligned to current renderable stack.
 * @param {Object} state
 * @param {Object} payload
 * @returns {void}
 */
const updateSeriesRenderableCompactOrder = (
  { seriesRenderableDeclaredByImageKey, seriesRenderableDeclaredCompact },
  { seriesId, imageIds, managerImageId, declaredSliceIndex } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return;
  }
  if (!seriesRenderableDeclaredByImageKey[seriesId]) {
    seriesRenderableDeclaredByImageKey[seriesId] = new Map();
  }
  const byImageKey = seriesRenderableDeclaredByImageKey[seriesId];
  if (
    managerImageId !== null &&
    managerImageId !== undefined &&
    Number.isInteger(declaredSliceIndex)
  ) {
    byImageKey.set(toImageKey(managerImageId), declaredSliceIndex);
  }

  if (!Array.isArray(imageIds) || imageIds.length === 0) {
    return;
  }
  seriesRenderableDeclaredCompact[seriesId] = imageIds.map(imageId => {
    const declared = byImageKey.get(toImageKey(imageId));
    return Number.isInteger(declared) && declared >= 0 ? declared : null;
  });
};

/**
 * Returns declared-order entries enriched with renderable position.
 * @param {Object} seriesRenderableDeclaredCompact
 * @param {string|number} seriesId
 * @returns {Array<{declared:number,position:number}>}
 */
const getRenderableDeclaredEntries = (seriesRenderableDeclaredCompact, seriesId) => {
  const compact = seriesRenderableDeclaredCompact[seriesId];
  if (!Array.isArray(compact) || compact.length === 0) {
    return [];
  }
  const entries = [];
  compact.forEach((declared, position) => {
    if (Number.isInteger(declared) && declared >= 0) {
      entries.push({ declared, position });
    }
  });
  return entries;
};

/**
 * Finds nearest renderable declared index to requested slice.
 * @param {number[]} renderableSliceIndexes
 * @param {number} requestedSliceId
 * @returns {{index:number|null,position:number|null}}
 */
const getNearestRenderableSliceIndex = (renderableSliceIndexes, requestedSliceId) => {
  if (!Array.isArray(renderableSliceIndexes) || !renderableSliceIndexes.length) {
    return { index: null, position: null };
  }
  let nearestPosition = 0;
  let nearestDistance = Math.abs(renderableSliceIndexes[0] - requestedSliceId);
  for (let position = 1; position < renderableSliceIndexes.length; position += 1) {
    const candidate = renderableSliceIndexes[position];
    const distance = Math.abs(candidate - requestedSliceId);
    if (
      distance < nearestDistance ||
      (distance === nearestDistance &&
        candidate < renderableSliceIndexes[nearestPosition])
    ) {
      nearestDistance = distance;
      nearestPosition = position;
    }
  }
  return {
    index: renderableSliceIndexes[nearestPosition],
    position: nearestPosition
  };
};

/**
 * Finds nearest renderable entry to requested declared slice.
 * @param {Array<{declared:number,position:number}>} entries
 * @param {number} requestedSliceId
 * @returns {{declared:number|null,position:number|null}}
 */
const getNearestRenderableEntry = (entries, requestedSliceId) => {
  if (!Array.isArray(entries) || !entries.length) {
    return { declared: null, position: null };
  }
  let nearest = entries[0];
  let nearestDistance = Math.abs(nearest.declared - requestedSliceId);
  for (let i = 1; i < entries.length; i += 1) {
    const candidate = entries[i];
    const distance = Math.abs(candidate.declared - requestedSliceId);
    if (
      distance < nearestDistance ||
      (distance === nearestDistance && candidate.declared < nearest.declared)
    ) {
      nearest = candidate;
      nearestDistance = distance;
    }
  }
  return nearest;
};

/**
 * Resolves best renderable target from requested declared slice.
 * @param {Object} state
 * @param {Object} payload
 * @returns {Object|null}
 */
const resolveSeriesRenderableSliceTarget = (
  {
    seriesDeclaredImageOrders,
    seriesRenderableDeclaredCompact,
    seriesDeclaredImageIndexMaps,
    seriesRenderableImageIds
  },
  { seriesId, requestedSliceId } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return null;
  }

  const declaredImageOrder = seriesDeclaredImageOrders[seriesId];
  if (!Array.isArray(declaredImageOrder) || declaredImageOrder.length === 0) {
    return null;
  }
  const declaredCount = declaredImageOrder.length;
  const declaredMaxSliceId = declaredCount > 0 ? declaredCount - 1 : null;
  const requested = Number.isFinite(requestedSliceId)
    ? Math.max(
        0,
        Math.floor(
          declaredMaxSliceId !== null
            ? Math.min(requestedSliceId, declaredMaxSliceId)
            : requestedSliceId
        )
      )
    : 0;
  const renderableDeclaredEntries = getRenderableDeclaredEntries(
    seriesRenderableDeclaredCompact,
    seriesId
  );
  if (renderableDeclaredEntries.length) {
    const requestedEntry = renderableDeclaredEntries.find(
      entry => entry.declared === requested
    );
    if (requestedEntry) {
      return {
        hasRenderable: true,
        requestedSliceId: requested,
        renderableSliceId: requestedEntry.declared,
        renderablePosition: requestedEntry.position,
        isRequestedRenderable: true,
        declaredCount,
        declaredMaxSliceId,
        renderableCount: renderableDeclaredEntries.length
      };
    }

    const nearestEntry = getNearestRenderableEntry(
      renderableDeclaredEntries,
      requested
    );
    return {
      hasRenderable: true,
      requestedSliceId: requested,
      renderableSliceId: nearestEntry.declared,
      renderablePosition: nearestEntry.position,
      isRequestedRenderable: false,
      declaredCount,
      declaredMaxSliceId,
      renderableCount: renderableDeclaredEntries.length
    };
  }

  const renderableSliceIndexes = getSeriesRenderableSliceIndexes(
    seriesDeclaredImageIndexMaps,
    seriesRenderableImageIds,
    seriesId
  );

  if (!renderableSliceIndexes.length) {
    return {
      hasRenderable: false,
      requestedSliceId: requested,
      renderableSliceId: null,
      renderablePosition: null,
      isRequestedRenderable: false,
      declaredCount,
      declaredMaxSliceId,
      renderableCount: 0
    };
  }

  const requestedPosition = renderableSliceIndexes.indexOf(requested);
  if (requestedPosition !== -1) {
    return {
      hasRenderable: true,
      requestedSliceId: requested,
      renderableSliceId: requested,
      renderablePosition: requestedPosition,
      isRequestedRenderable: true,
      declaredCount,
      declaredMaxSliceId,
      renderableCount: renderableSliceIndexes.length
    };
  }

  const nearest = getNearestRenderableSliceIndex(renderableSliceIndexes, requested);
  return {
    hasRenderable: true,
    requestedSliceId: requested,
    renderableSliceId: nearest.index,
    renderablePosition: nearest.position,
    isRequestedRenderable: false,
    declaredCount,
    declaredMaxSliceId,
    renderableCount: renderableSliceIndexes.length
  };
};

/**
 * Resolves declared slice index from a renderable stack position.
 * @param {Object} state
 * @param {Object} payload
 * @returns {number|null}
 */
const resolveSeriesDeclaredSliceIndexFromRenderablePosition = (
  { seriesRenderableDeclaredCompact, seriesDeclaredImageIndexMaps, seriesRenderableImageIds },
  { seriesId, renderablePosition } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return null;
  }
  if (!Number.isFinite(renderablePosition)) {
    return null;
  }
  const compactDeclared = seriesRenderableDeclaredCompact[seriesId];
  if (Array.isArray(compactDeclared) && compactDeclared.length > 0) {
    const position = Math.max(
      0,
      Math.min(compactDeclared.length - 1, Math.floor(renderablePosition))
    );
    const declaredSliceIndex = compactDeclared[position];
    if (Number.isInteger(declaredSliceIndex)) {
      return declaredSliceIndex;
    }
    for (let distance = 1; distance < compactDeclared.length; distance += 1) {
      const left = position - distance;
      if (left >= 0 && Number.isInteger(compactDeclared[left])) {
        return compactDeclared[left];
      }
      const right = position + distance;
      if (right < compactDeclared.length && Number.isInteger(compactDeclared[right])) {
        return compactDeclared[right];
      }
    }
    return null;
  }
  const renderableSliceIndexes = getSeriesRenderableSliceIndexes(
    seriesDeclaredImageIndexMaps,
    seriesRenderableImageIds,
    seriesId
  );
  if (!renderableSliceIndexes.length) {
    return null;
  }
  const position = Math.max(
    0,
    Math.min(renderableSliceIndexes.length - 1, Math.floor(renderablePosition))
  );
  const declaredSliceIndex = renderableSliceIndexes[position];
  return Number.isInteger(declaredSliceIndex) ? declaredSliceIndex : null;
};

/**
 * Resolves the next declared slice that is currently renderable.
 * @param {Object} state
 * @param {Object} payload
 * @returns {number|null}
 */
const resolveSeriesNextRenderableDeclaredSlice = (
  { seriesRenderableDeclaredCompact },
  { seriesId, currentDeclaredSliceIndex, direction = 1 } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return null;
  }
  const entries = getRenderableDeclaredEntries(seriesRenderableDeclaredCompact, seriesId);
  if (!entries.length) {
    return null;
  }
  const sortedDeclared = Array.from(
    new Set(entries.map(entry => entry.declared))
  ).sort((left, right) => left - right);
  if (!sortedDeclared.length) {
    return null;
  }
  const current = Number.isFinite(currentDeclaredSliceIndex)
    ? Math.floor(currentDeclaredSliceIndex)
    : sortedDeclared[0];
  if (direction < 0) {
    for (let i = sortedDeclared.length - 1; i >= 0; i -= 1) {
      if (sortedDeclared[i] < current) {
        return sortedDeclared[i];
      }
    }
    return sortedDeclared[0];
  }
  for (let i = 0; i < sortedDeclared.length; i += 1) {
    if (sortedDeclared[i] > current) {
      return sortedDeclared[i];
    }
  }
  return sortedDeclared[sortedDeclared.length - 1];
};

/**
 * Resolves a renderable declared slice constrained by navigation direction.
 * Direction < 0 resolves the closest renderable <= requested slice.
 * Direction > 0 resolves the closest renderable >= requested slice.
 * Direction === 0 resolves only exact matches.
 * @param {Object} state
 * @param {Object} payload
 * @returns {number|null}
 */
const resolveSeriesDirectionalRenderableDeclaredSlice = (
  { seriesRenderableDeclaredCompact },
  { seriesId, requestedSliceId, direction = 0 } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return null;
  }
  const entries = getRenderableDeclaredEntries(seriesRenderableDeclaredCompact, seriesId);
  if (!entries.length) {
    return null;
  }
  const sortedDeclared = Array.from(
    new Set(entries.map(entry => entry.declared))
  ).sort((left, right) => left - right);
  if (!sortedDeclared.length) {
    return null;
  }
  const requested = Number.isFinite(requestedSliceId)
    ? Math.floor(requestedSliceId)
    : sortedDeclared[0];
  if (direction < 0) {
    for (let i = sortedDeclared.length - 1; i >= 0; i -= 1) {
      if (sortedDeclared[i] <= requested) {
        return sortedDeclared[i];
      }
    }
    return null;
  }
  if (direction > 0) {
    for (let i = 0; i < sortedDeclared.length; i += 1) {
      if (sortedDeclared[i] >= requested) {
        return sortedDeclared[i];
      }
    }
    return null;
  }
  for (let i = 0; i < sortedDeclared.length; i += 1) {
    if (sortedDeclared[i] === requested) {
      return requested;
    }
  }
  return null;
};

/**
 * Resolves contiguous slider runs for currently renderable declared slices.
 * In 4D mode, frames are projected to the slice-axis using framesPerSlice.
 * @param {Object} state
 * @param {Object} payload
 * @returns {Array<{start:number,end:number}>}
 */
const resolveSeriesRenderableDeclaredSliceRuns = (
  {
    seriesRenderableDeclaredCompact,
    seriesDeclaredImageIndexMaps,
    seriesRenderableImageIds
  },
  { seriesId, framesPerSlice = 1, minSliceIndex = null, maxSliceIndex = null } = {}
) => {
  if (seriesId === null || seriesId === undefined) {
    return [];
  }
  const normalizedFramesPerSlice = normalizeFramesPerSlice(framesPerSlice);
  const entries = getRenderableDeclaredEntries(seriesRenderableDeclaredCompact, seriesId);
  const declaredIndexes = entries.length
    ? entries.map(entry => entry.declared)
    : getSeriesRenderableSliceIndexes(
        seriesDeclaredImageIndexMaps,
        seriesRenderableImageIds,
        seriesId
      );
  if (!declaredIndexes.length) {
    return [];
  }
  const normalizedMin = Number.isFinite(minSliceIndex)
    ? Math.max(0, Math.floor(minSliceIndex))
    : null;
  const normalizedMax = Number.isFinite(maxSliceIndex)
    ? Math.max(0, Math.floor(maxSliceIndex))
    : null;
  const projected = declaredIndexes
    .map(index =>
      normalizedFramesPerSlice > 1
        ? Math.floor(index / normalizedFramesPerSlice)
        : index
    )
    .filter(index => {
      if (!Number.isInteger(index) || index < 0) {
        return false;
      }
      if (normalizedMin !== null && index < normalizedMin) {
        return false;
      }
      if (normalizedMax !== null && index > normalizedMax) {
        return false;
      }
      return true;
    });
  return buildIndexRuns(projected);
};

export {
  addSeriesRenderableImage,
  buildIndexRuns,
  buildDeclaredImageIndexMap,
  buildPriorityPendingOrder,
  buildPriorityPendingOrderForTimeFrame,
  clampSliceIndex,
  getDeclaredSliceIndex,
  normalizeFramesPerSlice,
  resolveFramesPerSlice,
  resolveSeriesDirectionalRenderableDeclaredSlice,
  resolveSeriesRenderableDeclaredSliceRuns,
  resolveSeriesDeclaredSliceIndexFromRenderablePosition,
  resolveSeriesNextRenderableDeclaredSlice,
  resolveSeriesRenderableSliceTarget,
  buildPriorityPendingOrderForActiveAxis,
  toImageKey,
  uniqueImageIds,
  updateSeriesRenderableCompactOrder
};
